import { BWOD_MARKETS, MARKET } from 'shared/helpers/constants';

/**
 * Formats a number into a currency string
 *
 * @param dollars - The number to format
 * @param truncate - Whether to truncate the cents
 * @returns The formatted string
 */
export const formatCurrency = (dollars: number, truncate = true) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(truncate ? Math.trunc(dollars) : dollars); // `.trunc` to remove cents

/**
 * Formats a number into a percent string
 *
 * @param percentage - The number to format
 * @param minimumFractionDigits - The minimum number of digits to display after the decimal point
 * @returns The formatted string
 */
export const formatPercent = (percentage: number, minimumFractionDigits?: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: minimumFractionDigits ?? 1,
  }).format(percentage);

/**
 * Formats for thousands
 * TODO: backport to `bricks` `currencyHelpers`
 *
 * @param dollars - The number to format
 * @returns The formatted string
 */
export const formatThousands = (dollars: number) => `${formatCurrency(dollars / 1000)}k`;

/**
 * Format for millions
 * TODO: backport to `bricks` `currencyHelpers`
 *
 * @param dollars - The number to format
 * @returns The formatted string
 */
export const formatMillions = (dollars: number) => `${formatCurrency(dollars / 1_000_000, false)}M`;

/**
 * Formats a number into a currency string
 *
 * @param dollars - The number to format
 * @returns The formatted string
 */
export const formatPriceChange = (dollars: number) => {
  if (Math.abs(dollars) >= 1_000_000) {
    return formatMillions(dollars);
  }
  if (Math.abs(dollars) > 1_000) {
    return formatThousands(dollars);
  }
  return formatCurrency(dollars);
};

/**
 * Rounds a number to the nearest value
 *
 * @param value - The number to round
 * @param nearest - The nearest value to round to
 */
export const round = (value: number, nearest: number): number =>
  Math.round(value / nearest) * nearest;

/**
 * Takes in phone number format +1XXXXXXXXXX and transforms it into XXX-XXX-XXXX
 *
 * @param rawPhoneNumber - The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (rawPhoneNumber: string | null | undefined): string => {
  const cleaned = ('' + rawPhoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return '';
};

/**
 * Converts cents to dollars
 *
 * @param cents - The number of cents
 * @returns The number of dollars
 */
export const centsToDollars = (cents: number): number => Math.floor(cents / 100);

/**
 * Calculates the price change between two numbers
 *
 * @param originalPrice - The original price
 * @param finalPrice - The final price
 * @returns The price change
 */
export const calculatePriceChange = (originalPrice: number, finalPrice: number): number => {
  return Math.round(((finalPrice - originalPrice) / originalPrice) * 100);
};

/**
 *  Converts a snake case string to words
 *
 *  @returns The formatted string: "HELLO_HOW_ARE_YOU" -> "Hello How Are You"
 *  @param str - The string to format
 */
export const capitalizedSnakeCaseToWords = (str: string) => {
  return str
    .split('_')
    .map((word) => word[0].toUpperCase() + word.substr(1).toLowerCase())
    .join(' ');
};

/**
 * Capitalizes the given string
 *
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export const capitalizeString = (str: string): string => {
  if (str.length === 0) {
    return str;
  }
  return str[0].toUpperCase() + str.substr(1).toLowerCase();
};

/**
 * Transforms a BwOD market identifier into a human readable string
 * that can be used in the UI.
 *
 * @param market - The market identifier - e.g. "phoenix"
 * @returns - Human readable market name - e.g. "Phoenix, AZ"
 */
export const humanReadableBwODMarket = (market: MARKET | string): string => {
  const humanReadableMarket =
    BWOD_MARKETS.find((m) => m.value.toLowerCase() === market.toLowerCase())?.label ||
    capitalizeString(market);
  return humanReadableMarket;
};

/**
 * Given a human readable market name, only return the city name.
 *
 * @param marketName - The market name to parse
 * @returns - The city name
 */
export const getCityNameFromMarket = (marketName: string): string => {
  // Split the market name based on the comma
  // and join every element but the last one
  // Just in case the city name has a comma in it
  const splitArray = marketName.split(',');
  if (splitArray.length > 1) {
    return splitArray.slice(0, splitArray.length - 1).join(',');
  }
  return marketName;
};
